'use client';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ProductAccessoryDesktopTable } from './accessory-variants/desktop-table';
import { ProductAccessoryMobileTable } from './accessory-variants/mobile-table';
import type { ProductPriceSource } from '@eliadmin/core/src/utils/product-price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

type AccessoryVariant = ProductPriceSource & {
  id: number;
  slug: string;
  name: string;
  code?: string | null;
  availability: {
    id: number;
    name: string;
  };
  width?: number | string | null;
  height?: number | string | null;
  length?: number | string | null;
  values?:
    | {
        id: number;
        name?: string;
        attribute_name?: string;
        attribute_position?: number;
      }[]
    | null;
  attributes?:
    | {
        id: number;
        name: string;
        position?: number;
        values?:
          | {
              id: number;
              name?: string;
            }[]
          | null;
      }[]
    | null;
};

export type ProductAccessoryVariantRow = AccessoryVariant & {
  isCurrentProduct: boolean;
};

type ProductWithAccessoryVariants = ProductDetailData & {
  accessory_product_variants?: AccessoryVariant[] | null;
};

export function ProductAccessoryVariantsContainer({ product }: { product: ProductDetailData }) {
  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const typedProduct = product as ProductWithAccessoryVariants;
  const accessoryVariants = typedProduct.accessory_product_variants ?? [];

  if (accessoryVariants.length === 0) {
    return null;
  }

  const currentProductRow: ProductAccessoryVariantRow = {
    id: product.id,
    slug: product.slug,
    name: product.name,
    code: product.code,
    pricing: product.pricing,
    discounts: product.discounts,
    ask_for_price: product.ask_for_price,
    availability: product.availability,
    width: product.width,
    height: product.height,
    length: product.length,
    attributes: product.attributes?.map(attribute => ({
      id: attribute.id,
      name: attribute.name,
      position: attribute.position,
      values: attribute.values?.map(value => ({
        id: value.id,
        name: value.name,
      })),
    })),
    isCurrentProduct: true,
  };

  const variants: ProductAccessoryVariantRow[] = [
    currentProductRow,
    ...accessoryVariants.map(variant => ({
      ...variant,
      isCurrentProduct: false,
    })),
  ].sort((a, b) => {
    const aPrice = getProductPriceDisplay(a, { isWholesaler }).currentWithVat;
    const bPrice = getProductPriceDisplay(b, { isWholesaler }).currentWithVat;
    const priceDiff = (bPrice ?? Number.NEGATIVE_INFINITY) - (aPrice ?? Number.NEGATIVE_INFINITY);
    if (priceDiff !== 0) {
      return priceDiff;
    }

    return a.id - b.id;
  });

  return (
    <div className={cn('custom-section')}>
      <div className={cn('custom-container')}>
        <ProductAccessoryDesktopTable variants={variants} />
        <ProductAccessoryMobileTable variants={variants} />
      </div>
    </div>
  );
}
